import {DOMSH} from "lib/commons/xml/domsh";
import {EventMgr} from "lib/commons/events";


/**
 * Propriétés json de l'état précis d'un contexte graphique en vue de pouvoir être restaurée ultérieurement.
 */
export type JLastDatas = { [k: string]: Jsonisable }

/**
 * Construit un json de l'état en cours du widget en vue d'une restauration ultérieure.
 */
export interface ILastDatasBuilder {
	/**
	 * Si cette fonction est déclarée sur un élément dom, l'appelant ne parcourera pas ses noeuds fils et shadows,
	 * c'est à l'implémentation de cette fonction de le gérer.
	 *
	 * @see LASTDATAS.buildLastDatas()
	 */
	buildLastDatas?(parentLastDatas: JLastDatas): void
}

export function isLastDatasBuilder(o: any): o is ILastDatasBuilder {return o && typeof o.buildLastDatas === 'function'}

/**
 * Doit être implémenté par les containers de sous-widgets qui ajoutent un niveau de profondeur
 * à l'arbre des lastDatas via ILastDatasBuilder.buildLastDatas().
 *
 * Permet aux  sous-widgets de retrouver le "même" parentLastDatas qui avait été passé
 * en paramètre à ILastDatasBuilder.buildLastDatas(parentLastDatas: JLastDatas).
 */
export interface ILastDatasHolder extends HTMLElement {
	getParentLastDatas(forChild: HTMLElement): JLastDatas | null
}

export function isLastDatasHolder(o: any): o is ILastDatasHolder {return o && typeof o.getParentLastDatas === 'function'}

/** Paramètres d'initialisation d'un élément pour déclarer des lastDatas. */
export interface OLastDatasInit {

	/** Key à utiliser pour le buildLastDatas() */
	lastDatasKey?: string

	/** lastDatas pour l'initialisation (correspondant à l'objet pointé par lastDatasKey). */
	lastDatas?: JLastDatas
}

export namespace LASTDATAS {

	export const buildLastDatasHooks = new EventMgr<(datas: JLastDatas) => void>();

	/** Construction de tous les lastDatas pour la page entière. */
	export function buildDocumentLastDatas(): JLastDatas {
		const datas = buildLastDatas({}, document.body, true);
		buildLastDatasHooks.emit(datas);
		return datas;
	}

	/**
	 * Parcourt la descendance de from pour exécuter tous les ILastDatasBuilder.buildLastDatas()
	 * contenus dans from.
	 */
	export function buildLastDatas(datas: JLastDatas, from: Node, includeFrom?: boolean): JLastDatas {
		if (!from) return datas;
		if (includeFrom && isLastDatasBuilder(from)) {
			from.buildLastDatas(datas);
		} else {
			if (includeFrom && from instanceof Element && from.shadowRoot) buildLastDatas(datas, from.shadowRoot);
			const tw = from.ownerDocument.createTreeWalker(from);
			if (tw.nextNode()) while (true) {
				if (isLastDatasBuilder(tw.currentNode)) {
					tw.currentNode.buildLastDatas(datas);
					//On ne parcourt pas les fils.
					while (!tw.nextSibling()) if (tw.parentNode() === from) return datas;
				} else {
					if ((tw.currentNode as Element).shadowRoot) buildLastDatas(datas, (tw.currentNode as Element).shadowRoot);
					if (!tw.nextNode()) return datas;
				}
			}
		}
		return datas;
	}

	/**
	 * Retrouve dans le contexte le parentLastDatas pour relier les propriétés srockées dans le lastDatas.
	 */
	export function getParentLastDatas(forChild: HTMLElement): JLastDatas | null {
		const parent = DOMSH.findLogicalFlatParent(forChild, null, isLastDatasHolder);
		return parent ? parent.getParentLastDatas(forChild) : null;
	}

	/**
	 *  Usage type (cf IEltInitable.buildInitFromAtts()) :
	 *  buildInitFromAtts(init){
	 * 		init.lastDatas = LASTDATAS.getLastDatas<JLDXxx>(this, this.getAttribute("last-datas"));
	 * 	}
	 */
	export function getLastDatas<T extends JLastDatas>(forChild: HTMLElement, key: string): T | null {
		if (key == null) return null;
		const parent = getParentLastDatas(forChild);
		return parent ? parent[key] : null;
	}
}
